import Icon from '@/components/ui/AppIcon';

interface ProcessStep {
  step: number;
  title: string;
  description: string;
  timeframe: string;
  icon: string;
}

interface ProcessTimelineProps {
  className?: string;
}

const ProcessTimeline = ({ className = '' }: ProcessTimelineProps) => {
  const processSteps: ProcessStep[] = [
    {
      step: 1,
      title: 'Submit Your Property',
      description:
        'Fill out our simple form with basic property details. No obligation, completely confidential.',
      timeframe: '5 minutes',
      icon: 'DocumentTextIcon',
    },
    {
      step: 2,
      title: 'Property Evaluation',
      description:
        'Our team conducts a thorough analysis of your property and local market conditions.',
      timeframe: '24-48 hours',
      icon: 'MagnifyingGlassIcon',
    },
    {
      step: 3,
      title: 'Receive Cash Offer',
      description:
        'Get a fair, no-obligation cash offer with transparent breakdown of our evaluation.',
      timeframe: '2-3 days',
      icon: 'CurrencyDollarIcon',
    },
    {
      step: 4,
      title: 'Accept & Schedule',
      description: 'If you accept our offer, we schedule the closing at your convenience.',
      timeframe: '1 day',
      icon: 'CalendarDaysIcon',
    },
    {
      step: 5,
      title: 'Close & Get Paid',
      description:
        'Complete the paperwork and receive your cash. No repairs, no showings, no hassle.',
      timeframe: '30 days or less',
      icon: 'CheckCircleIcon',
    },
  ];

  return (
    <section id="process-timeline" className={`py-16 lg:py-24 bg-surface ${className}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-6">
            Our Simple 5-Step Process
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            From initial contact to cash in hand, we've streamlined the process to be as smooth and
            transparent as possible. No surprises, no hidden fees.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Desktop Timeline Line */}
          <div className="hidden lg:block absolute top-20 left-0 right-0 h-0.5 bg-border"></div>

          <div className="grid lg:grid-cols-5 gap-8">
            {processSteps.map((step, index) => (
              <div key={step.step} className="relative">
                {/* Mobile Timeline Line */}
                {index < processSteps.length - 1 && (
                  <div className="lg:hidden absolute left-6 top-20 w-0.5 h-24 bg-border"></div>
                )}

                {/* Step Card */}
                <div className="relative bg-card rounded-xl p-6 shadow-card border border-border silk-smooth hover:shadow-card">
                  {/* Step Number Circle */}
                  <div className="absolute -top-4 left-6 lg:left-1/2 lg:-translate-x-1/2 w-12 h-12 bg-primary rounded-full flex items-center justify-center shadow-button">
                    <span className="text-lg font-bold text-primary-foreground">{step.step}</span>
                  </div>

                  {/* Icon */}
                  <div className="flex justify-center mb-4 pt-4">
                    <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center">
                      <Icon name={step.icon as any} size={32} className="text-accent" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="text-center space-y-3">
                    <h3 className="text-lg font-semibold text-foreground">{step.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                    <div className="inline-flex items-center px-3 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                      <Icon name="ClockIcon" size={12} className="mr-1" />
                      {step.timeframe}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Process Benefits */}
        <div className="mt-16 grid md:grid-cols-3 gap-8">
          <div className="text-center p-6 bg-card rounded-lg border border-border">
            <Icon name="ShieldCheckIcon" size={48} className="text-primary mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Transparent Process</h3>
            <p className="text-sm text-muted-foreground">
              Every step explained clearly with no hidden surprises or fees.
            </p>
          </div>
          <div className="text-center p-6 bg-card rounded-lg border border-border">
            <Icon name="BoltIcon" size={48} className="text-accent mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">30 Days or Less</h3>
            <p className="text-sm text-muted-foreground">
              Close in 30 days or less when you need a direct, streamlined sale.
            </p>
          </div>
          <div className="text-center p-6 bg-card rounded-lg border border-border">
            <Icon name="HandRaisedIcon" size={48} className="text-success mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Obligations</h3>
            <p className="text-sm text-muted-foreground">
              Get your offer with zero commitment or pressure to sell.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
